import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import jsPDF from "jspdf";
import PrintIcon from "../../../../assets/icons/PrintIcon";
import DownloadIcon from "../../../../assets/icons/DownloadIcon";
import { toast } from "react-toastify";
import "../../../../style/returnsRegister.css";

function PrintReturnRegister({ isOpen, onClose, returnData }) {
  const barcodeRef = useRef(null);

  // Get IMEI number (use optional chaining to avoid errors if returnData is null)
  const imei = returnData?.imei || "-";
  const barcodeNumber =
    imei !== "-" ? imei : returnData?.return_id?.replace(/-/g, "") || "";

  // Generate barcode from IMEI
  useEffect(() => {
    if (
      isOpen &&
      returnData &&
      barcodeRef.current &&
      barcodeNumber &&
      barcodeNumber !== "-"
    ) {
      try {
        // Clear previous barcode
        barcodeRef.current.innerHTML = "";
        JsBarcode(barcodeRef.current, barcodeNumber, {
          format: "CODE128",
          width: 2,
          height: 80,
          displayValue: true,
          fontSize: 14,
          margin: 10,
        });
      } catch (error) {
        console.error("Error generating barcode:", error);
      }
    }
  }, [barcodeNumber, isOpen, returnData]);

  if (!isOpen || !returnData) return null;

  // Format date for display (e.g., "Oct 15, 2025")
  const formatReturnDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const dateObj =
        typeof dateString === "string" ? new Date(dateString) : dateString;
      if (isNaN(dateObj.getTime())) return dateString || "-";

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[dateObj.getMonth()];
      const day = dateObj.getDate();
      const year = dateObj.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch (error) {
      return dateString || "-";
    }
  };

  // Get return ID
  const returnId = returnData.return_id || returnData.id || "-";

  // Get device information - use product.display_name if available, otherwise construct from product data
  const product = returnData.product || {};
  const deviceInfo =
    (product.display_name && product.display_name.trim() !== "")
      ? product.display_name
      : returnData.product_name ||
        product.name ||
        returnData.model ||
        "-";

  // Get customer name
  const customer = returnData.customer_name || returnData.customer || "-";

  // Get defect note
  const defectNote =
    returnData.note ||
    returnData.defect_note ||
    returnData.defect ||
    returnData.notes ||
    "-";

  const handlePrint = () => {
    // Small delay to ensure barcode is fully rendered
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Add title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Return Registration", 105, 15, { align: "center" });

      // Add Return ID and Date
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Return ID: ${returnId}`, 14, 25);
      doc.text(
        `Date: ${formatReturnDate(
          returnData.buying_date ||
            returnData.created_at ||
            returnData.date ||
            returnData.return_date
        )}`,
        14,
        31
      );

      // Add IMEI
      if (imei && imei !== "-") {
        doc.text(`IMEI: ${imei}`, 14, 37);
      }

      let yPos = 50;

      // Add barcode if available
      if (barcodeNumber && barcodeNumber !== "-") {
        try {
          // Create a temporary canvas for barcode
          const canvas = document.createElement("canvas");

          // Generate barcode on canvas
          JsBarcode(canvas, barcodeNumber, {
            format: "CODE128",
            width: 2,
            height: 60,
            displayValue: true,
            fontSize: 14,
            margin: 5,
          });

          // Convert canvas to image
          const imgData = canvas.toDataURL("image/png");

          // Add barcode image to PDF (centered)
          const imgWidth = 60;
          const imgHeight = 25;
          doc.addImage(
            imgData,
            "PNG",
            (210 - imgWidth) / 2,
            yPos,
            imgWidth,
            imgHeight
          );

          // Add barcode number below image

          yPos += imgHeight + 15;
        } catch (error) {
          console.error("Error adding barcode to PDF:", error);
          // Continue without barcode
        }
      }

      // Add other details
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Device", 14, yPos);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const splitDevice = doc.splitTextToSize(deviceInfo, 180);
      doc.text(splitDevice, 14, yPos + 6);

      yPos += splitDevice.length * 6 + 8;

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Customer", 14, yPos);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(customer, 14, yPos + 6);

      yPos += 12;

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Defect Note", 14, yPos);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const splitDefectNote = doc.splitTextToSize(defectNote, 180);
      doc.text(splitDefectNote, 14, yPos + 6);

      // Save PDF
      const fileName = `Return_${returnId}_${new Date().getTime()}.pdf`;
      doc.save(fileName);

      toast.success("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="print-return-modal-overlay" onClick={handleCancel}>
      <div className="print-return-modal" onClick={(e) => e.stopPropagation()}>
        <div className="print-return-modal-content">
          <div className="print-return-modal-header">
            <div className="print-return-header-left">
              <div className="print-return-label">Return ID</div>
              <div className="print-return-value">{returnId}</div>
            </div>

            <div className="print-return-header-center">
              {barcodeNumber && barcodeNumber !== "-" ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <svg ref={barcodeRef} className="print-return-barcode" />
                </div>
              ) : (
                <div style={{ fontSize: "14px", color: "#6E7781" }}>
                  No IMEI available
                </div>
              )}
            </div>

            <div className="print-return-header-right">
              <div
                className="print-return-label"
                style={{ paddingLeft: "3.8rem" }}
              >
                Date
              </div>
              <div className="print-return-value">
                {formatReturnDate(
                  returnData.date ||
                    returnData.created_at ||
                    returnData.return_date
                )}
              </div>
            </div>
          </div>

          <div className="print-return-divider"></div>

          <div className="print-return-section">
            <div className="print-return-label">Device</div>
            <div className="print-return-value">{deviceInfo}</div>
          </div>

          <div className="print-return-divider"></div>

          <div className="print-return-section">
            <div className="print-return-label">Customer:</div>
            <div className="print-return-value">{customer}</div>
          </div>

          <div className="print-return-divider"></div>

          <div className="print-return-section">
            <div className="print-return-label">Defect Note</div>
            <div className="print-return-value">{defectNote}</div>
          </div>
        </div>

        <div className="print-return-modal-actions">
          <button className="print-return-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="print-return-export-btn" onClick={handleExportPDF}>
            <DownloadIcon />
            Export PDF
          </button>
          <button className="print-return-print-btn" onClick={handlePrint}>
            <PrintIcon />
            Print
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrintReturnRegister;
